import * as Popover from '@radix-ui/react-popover'
import {
    ComponentProps,
    MutableRefObject,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import useMeasure from 'react-use-measure'
import { twMerge as twm } from 'tailwind-merge'

import { useOnClickOutside } from '../../../hooks/useOnClickOutside'
import { useScrollHighlightedElementIntoView } from '../../../hooks/useScrollHighlightedElementIntoView'
import { Icon, IconName } from '../../Icon'
import { Spinner } from '../../Spinner'
import Button from '../../app/design/Button'
import Label from '../../app/design/Label'
import Paragraph from '../../app/design/Paragraph'
import { Checkbox } from './Checkbox'
import { TextField, TextFieldChip } from './TextField'

export type ComboBoxItem = {
    id: string | number
    title: string
    secondaryTitle?: string
    subtitle?: ReactNode
    isChecked?: boolean
    isDanger?: boolean
    isLoading?: boolean
    isDisabled?: boolean
    isNested?: boolean
} & ({ tertiaryView?: JSX.Element } | { icon?: IconName })

export type Props<ItemType extends ComboBoxItem, ChipType extends TextFieldChip = TextFieldChip> = {
    searchValue?: string
    onSearchValueChange?: (newValue: string) => void
    onClearSearchValue?: () => void
    size?: 'small' | 'medium'
    items?: ItemType[]
    selectedItem?: ItemType | null
    onSelectItem?: (item: ItemType | null) => void
    shouldPreserveLastSelection?: boolean
    titleWeight?: 'normal' | 'bold'
    inputRef?: MutableRefObject<HTMLInputElement>
    placeholder?: string
    className?: string
    align?: ComponentProps<typeof Popover.Content>['align']
    maxDropdownHeight?: number
    hasChevron?: boolean
    auxiliaryButtonTitle?: string
    onAuxiliaryButtonClick?: () => void
    isLoading?: boolean
    chips?: ChipType[]
    onRemoveChipWithID?: (chipID: string | number) => void
    shouldRefocusAfterSelection?: boolean
    shouldSelectAllTextOnClick?: boolean
    icon?: ReactNode
    secondaryButton?: ComponentProps<typeof TextField>['secondaryButton']
    shouldPortal?: boolean
    disabled?: boolean
}

export const ComboBox = <
    ItemType extends ComboBoxItem,
    ChipType extends TextFieldChip = TextFieldChip,
>({
    searchValue,
    onSearchValueChange,
    onClearSearchValue,
    size = 'medium',
    items,
    selectedItem,
    onSelectItem,
    shouldPreserveLastSelection = true,
    inputRef,
    placeholder,
    className,
    align = 'start',
    maxDropdownHeight,
    hasChevron = true,
    auxiliaryButtonTitle,
    onAuxiliaryButtonClick,
    isLoading = false,
    chips,
    onRemoveChipWithID,
    shouldRefocusAfterSelection = false,
    shouldSelectAllTextOnClick = false,
    icon,
    secondaryButton,
    shouldPortal = true,
    disabled = false,
}: Props<ItemType, ChipType>) => {
    // We want to keep the ComboBox open when either the text field or the dropdown has focus,
    // so this state is a reference counter for the number of fields with focus. Really only
    // one will have focus at a time, but we introduce a delay after a blur event before
    // decrementing the counter so that the other component has a chance to gain focus and
    // increment the counter again.
    const [focusCounter, setFocusCounter] = useState(0)
    const hasFocus = focusCounter > 0 && !disabled

    const [highlightedItemIndex, setHighlightedItemIndex] = useState(0)

    const containerRef = useRef<HTMLDivElement>(null)
    const scrollingContainerRef = useRef<HTMLDivElement>(null)

    const [elementHeight, setElementHeight] = useState<number | null>(null)

    const [auxiliaryButtonRef, auxiliaryButtonBoundingRect] = useMeasure()

    const textFieldID = useMemo(() => Math.random().toString(), [])

    useScrollHighlightedElementIntoView(scrollingContainerRef, [highlightedItemIndex, items])

    useOnClickOutside(
        containerRef,
        () => {
            if (disabled && focusCounter > 0) {
                setFocusCounter(0)
            }
        },
        { capture: false }
    )

    // This state helps manage the shouldPreserveLastSelection prop. If we want to preserve the
    // last selection, then when a user clicks on an already selected option we don't want to
    // simply clear the item. However, we do want to put this component back in "picker" mode.
    // This state helps us do that - this component is in "picker" mode whenever there is either
    // (1) no selected item or (2) this boolean is `true`.
    //
    // When the combo box is no longer focused, we reset this state to allow the component to
    // exit "picker" mode and show the selected item again.
    const [isSelectingNewItem, setIsSelectingNewItem] = useState(false)
    useEffect(() => {
        if (focusCounter > 0) {
            return
        }
        setIsSelectingNewItem(false)
    }, [focusCounter])

    useEffect(() => {
        if (!hasFocus) {
            return
        }

        setTimeout(() => {
            const scrollingContainer = scrollingContainerRef.current
            if (!scrollingContainer) {
                return
            }

            const firstElement = scrollingContainer.firstElementChild
            if (!firstElement) {
                return
            }

            setElementHeight(firstElement.getBoundingClientRect().height)
        })
    }, [hasFocus, items])

    const handleClearSelectedItem = () => {
        if (shouldPreserveLastSelection) {
            setIsSelectingNewItem(true)
        }
        else {
            onSelectItem?.(null)
        }
        setTimeout(() => {
            const textField = document.querySelector(
                `[data-combo-box-text-field-id='${textFieldID}']`
            )
            if (textField instanceof HTMLInputElement) {
                textField.focus()
                if (shouldSelectAllTextOnClick) {
                    textField.select()
                }
            }
        })
    }

    const [previousHasFocus, setPreviousHasFocus] = useState(false)
    if (hasFocus !== previousHasFocus) {
        setPreviousHasFocus(hasFocus)
        setHighlightedItemIndex(0)
    }

    const focusTextField = () => {
        const textField = document.querySelector(`[data-combo-box-text-field-id='${textFieldID}']`)
        if (!(textField instanceof HTMLInputElement)) {
            return
        }
        textField.focus()
    }

    const handleSelectItem = useCallback(
        (item: ItemType | null) => {
            setIsSelectingNewItem(false)
            onSelectItem?.(item)

            if (shouldRefocusAfterSelection) {
                const textField = document.querySelector(
                    `[data-combo-box-text-field-id='${textFieldID}']`
                )
                if (textField instanceof HTMLInputElement) {
                    setTimeout(() => {
                        textField.focus()
                    })
                }
            }
            else {
                setTimeout(() => {
                    const activeElement = document.activeElement
                    if (activeElement instanceof HTMLElement) {
                        activeElement.blur()
                    }
                })
            }
        },
        [onSelectItem, shouldRefocusAfterSelection, textFieldID]
    )

    useEffect(() => {
        if (!hasFocus || items?.length === undefined) return

        const abortController = new AbortController()

        document.addEventListener(
            'keydown',
            (event) => {
                if (event.key === 'ArrowDown' || (event.key === 'n' && event.ctrlKey)) {
                    setHighlightedItemIndex((prev) => (prev + 1) % items.length)
                }
                else if (event.key === 'ArrowUp' || (event.key === 'p' && event.ctrlKey)) {
                    setHighlightedItemIndex((prev) => (prev - 1 + items.length) % items.length)
                }
                else if (event.key === 'Enter') {
                    const item = items[highlightedItemIndex]
                    if (item !== undefined) {
                        handleSelectItem(item)
                    }
                }
                else if (event.key === 'Escape') {
                    const textField = document.querySelector(
                        `[data-combo-box-text-field-id='${textFieldID}']`
                    )
                    if (textField instanceof HTMLInputElement) {
                        textField.blur()
                    }
                }
            },
            { signal: abortController.signal }
        )

        return () => abortController.abort()
    }, [hasFocus, items?.length, items, highlightedItemIndex, textFieldID, handleSelectItem])

    const maxHeight = (() => {
        if (maxDropdownHeight !== undefined) {
            return maxDropdownHeight
        }

        if (elementHeight === null) {
            return 440
        }

        return elementHeight * 5 + 16
    })()

    const content = (
        <Popover.Content
            onOpenAutoFocus={(event) => event.preventDefault()}
            sideOffset={8}
            align={align}
            avoidCollisions
            className="w-[var(--radix-popover-trigger-width)] overflow-y-auto rounded-lg bg-white p-2 shadow-xl"
            style={{
                maxHeight: maxHeight
                    ? `min(calc(var(--radix-popover-content-available-height) - 80px), ${maxHeight}px)`
                    : `calc(var(--radix-popover-content-available-height) - 80px)`,
            }}
            ref={scrollingContainerRef}
            onFocus={() => setFocusCounter((prev) => prev + 1)}
            onBlur={() => setTimeout(() => setFocusCounter((prev) => prev - 1))}
        >
            {items === undefined || items.length === 0 ? (
                <div className="text-center text-gray-600">No results</div>
            ) : (
                items.map((item, index) => (
                    <ComboBoxItemView
                        key={item.id}
                        item={item}
                        size={size}
                        isHighlighted={index === highlightedItemIndex}
                        onSelect={() => handleSelectItem(item)}
                        onMouseEnter={() => setHighlightedItemIndex(index)}
                    />
                ))
            )}
        </Popover.Content>
    )

    return (
        <div ref={containerRef} className={twm(className)}>
            <div className={twm(!!selectedItem && !isSelectingNewItem && 'hidden')}>
                <Popover.Root open={hasFocus}>
                    <Popover.Anchor asChild>
                        <div className="relative">
                            <TextField
                                ref={inputRef}
                                value={searchValue}
                                onChange={(event) => onSearchValueChange?.(event.target.value)}
                                size={size}
                                placeholder={placeholder}
                                isFullWidth
                                onFocus={() => !disabled && setFocusCounter((prev) => prev + 1)}
                                onBlur={() => {
                                    if (disabled) {
                                        setTimeout(() => setFocusCounter(0), 0)
                                    }
                                    else {
                                        setTimeout(() => setFocusCounter((prev) => prev - 1))
                                    }
                                }}
                                autoComplete="off"
                                data-combo-box-text-field-id={textFieldID}
                                data-1p-ignore
                                style={{
                                    paddingRight:
                                        (hasChevron ? 32 : 0) +
                                        28 +
                                        auxiliaryButtonBoundingRect.width,
                                }}
                                chips={chips}
                                onRemoveChipWithID={onRemoveChipWithID}
                                onClear={onClearSearchValue}
                                clearButtonOffset={hasChevron ? 40 : 0}
                                isLoading={isLoading}
                                icon={icon}
                                secondaryButton={hasFocus ? secondaryButton : undefined}
                                disabled={disabled}
                            />

                            <div className="absolute top-0 right-0 bottom-0 flex items-center justify-end pr-4 pl-2">
                                {auxiliaryButtonTitle !== undefined && (
                                    <Button
                                        intent="tertiaryLink"
                                        size="small"
                                        ref={auxiliaryButtonRef}
                                        onClick={onAuxiliaryButtonClick}
                                    >
                                        {auxiliaryButtonTitle}
                                    </Button>
                                )}

                                {hasChevron && (
                                    <Icon
                                        icon={
                                            size === 'small'
                                                ? 'chevronDown.small'
                                                : 'chevronDown.medium'
                                        }
                                        className={twm(
                                            'text-gray-700 transition-transform',
                                            hasFocus && 'rotate-180',
                                            disabled && 'text-gray-400'
                                        )}
                                        onClick={focusTextField}
                                    />
                                )}
                            </div>
                        </div>
                    </Popover.Anchor>
                    {shouldPortal ? <Popover.Portal>{content}</Popover.Portal> : content}
                </Popover.Root>
            </div>

            {!!selectedItem && !isSelectingNewItem && (
                <ComboBoxItemView
                    item={selectedItem}
                    size={size}
                    isHighlighted={false}
                    hasCaret
                    onSelect={handleClearSelectedItem}
                    className="hover:border-primary-700 hover:outline-primary-700 cursor-pointer rounded-lg border border-gray-500 bg-white outline-0 outline-gray-500 hover:outline-1"
                    disabled={disabled}
                />
            )}
        </div>
    )
}

const ComboBoxItemView = ({
    item,
    size,
    isHighlighted,
    onSelect,
    onMouseEnter,
    hasCaret = false,
    className,
    disabled,
}: {
    item: ComboBoxItem
    size: 'small' | 'medium'
    isHighlighted: boolean
    onSelect?: () => void
    onMouseEnter?: () => void
    hasCaret?: boolean
    className?: string
    disabled?: boolean
}) => {
    const tertiaryView = item.isDanger ? (
        <Icon icon="close.small" className="text-danger-400" />
    ) : 'tertiaryView' in item && item.tertiaryView !== undefined ? (
        item.tertiaryView
    ) : 'icon' in item && item.icon !== undefined ? (
        <Icon icon={`${item.icon}.small`} />
    ) : undefined

    return (
        <div
            className={twm(
                'grid cursor-pointer grid-cols-[auto_1fr_auto] gap-y-0.5 rounded-lg px-4 select-none',
                size === 'small' ? 'py-[7px]' : 'py-[11px]',
                isHighlighted && 'bg-gray-700/4',
                (disabled || item.isDisabled) && 'pointer-events-none opacity-30',
                item.isNested && 'pl-15',
                className
            )}
            onMouseEnter={onMouseEnter}
            onMouseDown={onSelect}
            data-is-highlighted={isHighlighted}
        >
            {tertiaryView ? (
                <div className="row-span-2 mr-3 flex items-center justify-center">
                    {tertiaryView}
                </div>
            ) : (
                <div className="row-span-2" />
            )}

            <Paragraph
                size={size === 'small' ? 'sm' : 'md'}
                className={twm(
                    'min-w-0 cursor-pointer self-center truncate font-normal',
                    item.subtitle === undefined && 'row-span-2',
                    item.isDanger && 'text-danger-400'
                )}
            >
                {item.title}

                {item.secondaryTitle && (
                    <span className="ml-1 text-gray-500">{item.secondaryTitle}</span>
                )}
            </Paragraph>

            {item.isLoading ? (
                <div className="row-span-2 ml-3 flex items-start justify-center">
                    <Spinner size={20} />
                </div>
            ) : item.isChecked !== undefined ? (
                <div className="row-span-2 ml-3 flex items-start justify-center">
                    <Checkbox size="sm" isChecked={item.isChecked} />
                </div>
            ) : hasCaret ? (
                <div className="row-span-2 ml-3 flex items-center justify-center">
                    <Icon
                        icon={size === 'small' ? 'chevronDown.small' : 'chevronDown.medium'}
                        className="text-gray-700"
                    />
                </div>
            ) : (
                <div className="row-span-2" />
            )}

            {item.subtitle && (
                <Label size="sm" className="cursor-pointer truncate font-normal text-gray-600">
                    {item.subtitle}
                </Label>
            )}
        </div>
    )
}
